import { useRef, useState } from "react"
import propTypes from "prop-types"
import {
  Alert,
  Button,
  Col,
  Input,
  InputGroup,
  InputGroupText,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap"
import Swal from "sweetalert2"
import { useHistory } from "react-router"
import { useQueryClient, useMutation } from "@tanstack/react-query"

import { deleteTicket } from "services/tickets"
import { deleteCreditAccount, editCreditAccount, getCreditAccount, payCreditAccount } from "services/credit-accounts"
import { lenguage, dateConfig } from "config/format-date"
import { formatPaymentMethodLabel } from "utils/payment-methods"
import { Loading } from "components"
import "assets/scss/custom/components/payment-flow-modal.scss"

const PAYMENT_METHODS = [
  { key: "cash", value: "01 - Efectivo.", label: "Efectivo" },
  { key: "card", value: "28 - Tarjeta de débito.", label: "Tarjeta debito/credito" },
  { key: "transfer", value: "03 - Transferencia electrónica de fondos.", label: "Transferencia" },
  { key: "other", value: "99 - Por definir.", label: "Otros" },
]

const STATUS_LABELS = {
  active: "Pagare",
  paid: "Pagare pagado",
  deleted: "Pagare eliminado",
}

const PendingPaidItemList = ({
  item,
  setTicketPaying,
  openTicketModal,
  isCashOperationDayOpen,
  showReceivableAmounts,
}) => {
  const ticket = showReceivableAmounts ? item.ticket : item
  const date = new Date(String(ticket.date || "").replace("Z", ""))
  const formattedDate = date.toLocaleString(lenguage, dateConfig)
  const history = useHistory()
  const queryClient = useQueryClient()

  const [paymentModalOpen, setPaymentModalOpen] = useState(false)
  const [historyModalOpen, setHistoryModalOpen] = useState(false)
  const [historyAccount, setHistoryAccount] = useState(null)
  const [paymentAmounts, setPaymentAmounts] = useState({
    cash: "",
    card: "",
    transfer: "",
    other: "",
  })
  const [discountAmount, setDiscountAmount] = useState("")
  const [paymentOtherDescription, setPaymentOtherDescription] = useState("")
  const [paymentNote, setPaymentNote] = useState("")
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null)
  const [isSavingPayment, setIsSavingPayment] = useState(false)
  const paymentInputRefs = {
    cash: useRef(null),
    card: useRef(null),
    transfer: useRef(null),
    other: useRef(null),
  }

  const styleCenterTable = { textAlign: "center", verticalAlign: "middle", padding: "0.3rem" }
  const actionButtonStyle = {
    width: "30px",
    height: "30px",
    padding: 0,
    lineHeight: 1,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  }
  const actionIconStyle = { fontSize: "0.85rem" }

  function currencyFormatter({ currency, value }) {
    const formatter = new Intl.NumberFormat("en-US", {
      style: "currency",
      minimumFractionDigits: 2,
      currency,
    })
    return formatter.format(Number(value || 0))
  }

  const pendingAmount = Number(item.pending_amount || 0)
  const paymentTotal = PAYMENT_METHODS.reduce(
    (total, method) => total + Number(paymentAmounts[method.key] || 0),
    0
  )
  const currentDiscount = Number(item.discount || 0)
  const discountTotal = Number(discountAmount || 0)
  const totalApplied = paymentTotal + discountTotal
  const remainingAfterPayment = Math.max(pendingAmount - totalApplied, 0)
  const isPaymentInvalid = totalApplied <= 0 || totalApplied > pendingAmount
  const historyDiscount = Number(historyAccount?.discount || item.discount || 0)

  const formatDateTime = (value) => {
    if (!value) return "-"
    const paymentDate = new Date(String(value).replace("Z", ""))
    if (Number.isNaN(paymentDate.getTime())) return "-"
    return paymentDate.toLocaleString(lenguage, dateConfig)
  }

  const getPaymentDisplayLabel = (payment) => {
    return payment.pay_method_label || formatPaymentMethodLabel(payment.pay_method)
  }

  const updatePaymentAmount = (key, value) => {
    const nextValue = Math.abs(Number(value || 0))
    setPaymentAmounts((current) => ({
      ...current,
      [key]: value === "" ? "" : nextValue,
    }))
  }

  const setExclusivePaymentAmount = (paymentMethod, amount) => {
    setPaymentAmounts({
      cash: paymentMethod === "cash" ? amount : "",
      card: paymentMethod === "card" ? amount : "",
      transfer: paymentMethod === "transfer" ? amount : "",
      other: paymentMethod === "other" ? amount : "",
    })
  }

  const cyclePaymentAmount = () => {
    if (!selectedPaymentMethod) {
      return
    }

    const nextPaymentMethod = selectedPaymentMethod === "cash"
      ? "card"
      : selectedPaymentMethod === "card"
      ? "transfer"
      : selectedPaymentMethod === "transfer"
      ? "other"
      : "cash"

    setExclusivePaymentAmount(nextPaymentMethod, pendingAmount)
    setSelectedPaymentMethod(nextPaymentMethod)
    window.setTimeout(() => paymentInputRefs[nextPaymentMethod]?.current?.focus(), 0)
  }

  const handlePaymentMethodClick = (paymentMethod) => {
    setSelectedPaymentMethod(paymentMethod)
  }

  const handlePaymentMethodKeyDown = (event) => {
    if (event.key !== "Tab" || !selectedPaymentMethod) {
      return
    }

    event.preventDefault()
    cyclePaymentAmount()
  }

  const getPaymentInputStyle = (paymentMethod) => {
    if (selectedPaymentMethod !== paymentMethod) {
      return undefined
    }

    return {
      borderColor: "#0d6efd",
      boxShadow: "0 0 0 0.2rem rgba(13, 110, 253, 0.25)",
      backgroundColor: "#e7f1ff",
      color: "#084298",
      fontWeight: 600,
    }
  }

  const resetPaymentForm = () => {
    setPaymentAmounts({
      cash: "",
      card: "",
      transfer: "",
      other: "",
    })
    setDiscountAmount("")
    setPaymentNote("")
    setPaymentOtherDescription("")
    setSelectedPaymentMethod(null)
    setIsSavingPayment(false)
  }

  const deleteMutation = useMutation((id) => deleteTicket(id), {
    onSuccess: () => {
      queryClient.invalidateQueries(["ticketsNoPaid"])
      queryClient.invalidateQueries(["ticketsReceivable"])
      queryClient.invalidateQueries(["pendingTickets"])
      history.replace("/sale")
    },
    onError: () => {
      Swal.fire({
        title: "Error al eliminar el ticket",
        icon: "error",
        confirmButtonColor: "#6fc33a",
      })
    },
  })

  const handleOnClickSale = () => {
    setTicketPaying(ticket)
  }

  const handleOnClickCancel = (id) => {
    const numTicket = ticket.folio
    Swal.fire({
      title: `Â¿Estas seguro de eliminar el ticket NÂ°${numTicket}?`,
      text: "Se eliminara permanentemente!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#6fc33a",
      cancelButtonColor: "#d33",
      confirmButtonText: "Si, Eliminar el ticket!",
      cancelButtonText: "No, Cancelar!",
    }).then((result) => {
      if (result.isConfirmed) {
        deleteMutation.mutate(id)
        Swal.fire({
          title: `El ticket NÂ°${numTicket} se ha eliminado`,
          icon: "success",
          confirmButtonColor: "#6fc33a",
        })
      }
    })
  }

  const handleOpenPaymentModal = () => {
    setPaymentAmounts({
      cash: String(Number(item.pending_amount || 0)),
      card: "",
      transfer: "",
      other: "",
    })
    setDiscountAmount("")
    setSelectedPaymentMethod("cash")
    setPaymentModalOpen(true)
    window.setTimeout(() => {
      paymentInputRefs.cash.current?.focus()
      paymentInputRefs.cash.current?.select()
    }, 0)
  }

  const handlePayCreditAccount = async () => {
    if (isPaymentInvalid) {
      Swal.fire({
        icon: "warning",
        title: "Revisa el abono",
        text: totalApplied > pendingAmount
          ? "El abono y descuento no pueden ser mayores al saldo pendiente."
          : "Captura al menos una forma de pago.",
        confirmButtonColor: "#6fc33a",
      })
      return
    }

    const paymentsToSend = PAYMENT_METHODS
      .map((method) => ({
        amount: Number(paymentAmounts[method.key] || 0),
        pay_method: method.value,
        note: method.key === "other" && paymentOtherDescription
          ? [paymentOtherDescription, paymentNote].filter(Boolean).join(" - ")
          : paymentNote,
      }))
      .filter((payment) => payment.amount > 0)

    setIsSavingPayment(true)

    try {
      if (discountTotal > 0) {
        await editCreditAccount(item.id, {
          discount: currentDiscount + discountTotal,
        })
      }
      for (const payment of paymentsToSend) {
        await payCreditAccount(item.id, payment)
      }
      queryClient.invalidateQueries(["ticketsReceivable"])
      queryClient.invalidateQueries(["cash-register-balance"])
      setIsSavingPayment(false)
      setPaymentModalOpen(false)
      resetPaymentForm()
      Swal.fire({ icon: "success", title: "Abono registrado", confirmButtonColor: "#6fc33a" })
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "No se pudo registrar el abono",
        text: error?.message ? String(error.message) : "Intenta nuevamente",
        confirmButtonColor: "#6fc33a",
      })
    } finally {
      setIsSavingPayment(false)
    }
  }

  const handleDeleteCreditAccount = async () => {
    const result = await Swal.fire({
      title: `Eliminar pagare ${ticket.folio}?`,
      text: "El pagare quedara marcado como eliminado.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#6fc33a",
      cancelButtonColor: "#d33",
      confirmButtonText: "Si, eliminar",
      cancelButtonText: "Cancelar",
    })
    if (!result.isConfirmed) return

    try {
      await deleteCreditAccount(item.id)
      queryClient.invalidateQueries(["ticketsReceivable"])
      Swal.fire({ icon: "success", title: "Pagare eliminado", confirmButtonColor: "#6fc33a" })
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "No se pudo eliminar el pagare",
        text: error?.message ? String(error.message) : "Intenta nuevamente",
        confirmButtonColor: "#6fc33a",
      })
    }
  }

  const handleOpenHistory = async () => {
    try {
      const data = await getCreditAccount(item.id)
      setHistoryAccount(data)
      setHistoryModalOpen(true)
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "No se pudo cargar el historial",
        text: error?.message ? String(error.message) : "Intenta nuevamente",
        confirmButtonColor: "#6fc33a",
      })
    }
  }

  return (
    <>
      <tr key={item.id}>
        <td style={styleCenterTable}>
          <span
            onClick={() => openTicketModal(ticket.id)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault()
                openTicketModal(ticket.id)
              }
            }}
            role="button"
            tabIndex={0}
            style={{ color: "#007bff", cursor: "pointer" }}
          >
            {ticket.folio}
          </span>
        </td>
        <td style={styleCenterTable} className="text-cell">
          {formattedDate}
        </td>
        <td style={styleCenterTable} className="text-cell">
          {ticket?.user?.first_name} {ticket?.user?.last_name}
        </td>
        <td style={styleCenterTable} className="text-cell">
          {ticket?.customer?.name}
        </td>
        {showReceivableAmounts ? (
          <>
            <td style={styleCenterTable} className="money-cell">
              {currencyFormatter({ currency: "USD", value: item.amount })}
            </td>
            <td style={styleCenterTable} className="money-cell">
              {currencyFormatter({ currency: "USD", value: item.discount })}
            </td>
            <td style={styleCenterTable} className="money-cell">
              {currencyFormatter({ currency: "USD", value: item.deposit })}
            </td>
            <td style={styleCenterTable} className="money-cell">
              {currencyFormatter({ currency: "USD", value: item.pending_amount })}
            </td>
            <td style={styleCenterTable}>{STATUS_LABELS[item.status] || item.status}</td>
          </>
        ) : (
          <td style={styleCenterTable} className="money-cell">
            {currencyFormatter({ currency: "USD", value: ticket.total })}
          </td>
        )}
        <td style={styleCenterTable}>
          {isCashOperationDayOpen ? (
            <>
              {showReceivableAmounts ? (
                <>
                  <Button
                    color="primary"
                    size="sm"
                    style={actionButtonStyle}
                    title="Abonar"
                    disabled={item.status !== "active"}
                    onClick={handleOpenPaymentModal}
                  >
                    <i className="fa fa-dollar-sign" style={actionIconStyle} />
                  </Button>
                  <Button
                    color="info"
                    size="sm"
                    style={{ ...actionButtonStyle, marginLeft: "8px" }}
                    title="Historial de abonos"
                    onClick={handleOpenHistory}
                  >
                    <i className="fa fa-list" style={actionIconStyle} />
                  </Button>
                </>
              ) : (
                <Button
                  color="primary"
                  size="sm"
                  style={actionButtonStyle}
                  title="Continuar venta"
                  onClick={handleOnClickSale}
                >
                  <i className="fa fa-dollar-sign" style={actionIconStyle} />
                </Button>
              )}
              <Button
                style={{ ...actionButtonStyle, marginLeft: "8px" }}
                size="sm"
                color="danger"
                title={showReceivableAmounts ? "Eliminar pagare" : "Eliminar venta"}
                onClick={() => (showReceivableAmounts ? handleDeleteCreditAccount() : handleOnClickCancel(ticket.id))}
              >
                <i className="fa fa-trash" style={actionIconStyle} />
              </Button>
            </>
          ) : (
            <span className="text-muted">-</span>
          )}
        </td>
      </tr>
      {paymentModalOpen && (
        <Modal
          isOpen={paymentModalOpen}
          toggle={() => {
            if (!isSavingPayment) {
              setPaymentModalOpen(false)
            }
          }}
          centered
          size="lg"
          className="payment-flow-modal"
          backdrop="static"
          keyboard={!isSavingPayment}
        >
          <ModalHeader toggle={() => {
            if (!isSavingPayment) {
              setPaymentModalOpen(false)
            }
          }}>Aplicar Abono</ModalHeader>
          <ModalBody
            onMouseDown={(event) => {
              if (!event.target.closest("[data-payment-method]")) {
                setSelectedPaymentMethod(null)
              }
            }}
          >
            {isSavingPayment ? (
              <div className="text-center py-5">
                <Loading isLoading size={2} />
                <div className="mt-3 fw-semibold">Procesando pago en cuenta por cobrar...</div>
              </div>
            ) : (
              <>
            <div className="payment-flow-layout">
              <div className="payment-flow-panel">
            <Row className="g-3 align-items-center">
              <Col className="text-end">
                <Label>Descuento:</Label>
              </Col>
              <Col>
                <InputGroup>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={discountAmount}
                    onChange={(event) => (
                      setDiscountAmount(event.target.value === "" ? "" : Math.abs(Number(event.target.value || 0)))
                    )}
                  />
                  <InputGroupText>$</InputGroupText>
                </InputGroup>
              </Col>
            </Row>
            <br />
            <Label>
              <h4>Montos por Método de Pago</h4>
            </Label>
            {PAYMENT_METHODS.map((method) => (
              <div key={method.key} className="payment-flow-method-block">
                <Row
                  className={`g-3 align-items-center mb-3 ${
                    method.key === "other" ? "payment-flow-other-row" : ""
                  }`}
                >
                  {method.key === "other" ? (
                    <Col>
                      <Input
                        type="text"
                        value={paymentOtherDescription}
                        onChange={(event) => setPaymentOtherDescription(event.target.value)}
                        placeholder="Otros..."
                        className="text-end"
                      />
                    </Col>
                  ) : (
                    <Col className="text-end">
                      <Label className="mb-0">{method.label}:</Label>
                    </Col>
                  )}
                  <Col>
                    <InputGroup>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        innerRef={paymentInputRefs[method.key]}
                        data-payment-method={method.key}
                        value={paymentAmounts[method.key]}
                        style={getPaymentInputStyle(method.key)}
                        onClick={() => handlePaymentMethodClick(method.key)}
                        onFocus={() => handlePaymentMethodClick(method.key)}
                        onKeyDown={handlePaymentMethodKeyDown}
                        onChange={(event) => updatePaymentAmount(method.key, event.target.value)}
                      />
                      <InputGroupText>$</InputGroupText>
                    </InputGroup>
                  </Col>
                </Row>
              </div>
            ))}

            <Row className="g-3 align-items-center mt-2">
              <Col className="text-end">
                <Label>Nota:</Label>
              </Col>
              <Col>
                <Input value={paymentNote} onChange={(event) => setPaymentNote(event.target.value)} />
              </Col>
            </Row>
            <br />
              </div>
              <div className="payment-flow-summary">
                <div className="payment-flow-summary-row">
                  <span>Total pagare</span>
                  <strong>{currencyFormatter({ currency: "USD", value: item.amount })}</strong>
                </div>
                <div className="payment-flow-summary-row">
                  <span>Abonado</span>
                  <strong>{currencyFormatter({ currency: "USD", value: item.deposit })}</strong>
                </div>
                <div className="payment-flow-summary-row">
                  <span>Pendiente</span>
                  <strong>{currencyFormatter({ currency: "USD", value: pendingAmount })}</strong>
                </div>
                <div className="payment-flow-summary-row">
                  <span>Descuento</span>
                  <strong>{currencyFormatter({ currency: "USD", value: currentDiscount + discountTotal })}</strong>
                </div>
                <div className="payment-flow-summary-row">
                  <span>Monto recibido</span>
                  <strong>{currencyFormatter({ currency: "USD", value: paymentTotal })}</strong>
                </div>
                <div className="payment-flow-summary-row total">
                  <span>Saldo pendiente despues</span>
                  <strong>{currencyFormatter({ currency: "USD", value: remainingAfterPayment })}</strong>
                </div>
                <div className="payment-flow-summary-row customer">
                  <span>Cliente</span>
                  <strong>{ticket?.customer?.name || "-"}</strong>
                </div>
              </div>
            </div>
            {totalApplied > pendingAmount && (
              <Alert color="warning" className="mt-3 mb-0">
                El abono y descuento no pueden ser mayores al saldo pendiente.
              </Alert>
            )}
              </>
            )}
          </ModalBody>
          {!isSavingPayment && (
            <ModalFooter>
            <Button color="success" onClick={handlePayCreditAccount} disabled={isPaymentInvalid}>
              Guardar abono
            </Button>
            <Button color="secondary" onClick={() => setPaymentModalOpen(false)}>
              Cerrar
            </Button>
            </ModalFooter>
          )}
        </Modal>
      )}

      {historyModalOpen && (
        <Modal isOpen={historyModalOpen} toggle={() => setHistoryModalOpen(false)} centered size="lg">
          <ModalHeader toggle={() => setHistoryModalOpen(false)}>Historial de abonos</ModalHeader>
          <ModalBody style={{ maxHeight: "70vh", overflowY: "auto" }}>
            <Row className="mb-4">
              <Col md="4">
                <div className="text-muted small">Folio</div>
                <div className="fw-semibold">{ticket.folio}</div>
              </Col>
              <Col md="4">
                <div className="text-muted small">Cliente</div>
                <div className="fw-semibold">{ticket?.customer?.name || "-"}</div>
              </Col>
              <Col md="4">
                <div className="text-muted small">Estado</div>
                <div className="fw-semibold">{STATUS_LABELS[historyAccount?.status] || STATUS_LABELS[item.status] || "-"}</div>
              </Col>
              <Col md="3" className="mt-3">
                <div className="text-muted small">Total pagare</div>
                <div className="fw-semibold">{currencyFormatter({ currency: "USD", value: historyAccount?.amount || item.amount })}</div>
              </Col>
              <Col md="3" className="mt-3">
                <div className="text-muted small">Abonado</div>
                <div className="fw-semibold">{currencyFormatter({ currency: "USD", value: historyAccount?.deposit || item.deposit })}</div>
              </Col>
              <Col md="3" className="mt-3">
                <div className="text-muted small">Descuento</div>
                <div className="fw-semibold">{currencyFormatter({ currency: "USD", value: historyDiscount })}</div>
              </Col>
              <Col md="3" className="mt-3">
                <div className="text-muted small">Pendiente</div>
                <div className="fw-semibold">{currencyFormatter({ currency: "USD", value: historyAccount?.pending_amount || item.pending_amount })}</div>
              </Col>
            </Row>
            {historyDiscount > 0 && (
              <div className="border rounded p-3 mb-3">
                <div className="d-flex flex-column flex-md-row justify-content-between gap-2">
                  <div>
                    <div className="text-muted small">Concepto</div>
                    <div className="fw-semibold">Descuento aplicado</div>
                  </div>
                  <div className="text-md-end">
                    <div className="text-muted small">Monto</div>
                    <div className="fw-semibold">{currencyFormatter({ currency: "USD", value: historyDiscount })}</div>
                  </div>
                </div>
                <div className="text-muted small mt-2">Este importe reduce el pendiente, pero no cuenta como abonado.</div>
              </div>
            )}
            {historyAccount?.payments?.length ? (
              historyAccount.payments.map((payment) => (
                <div key={payment.id} className="border rounded p-3 mb-3">
                  <div className="d-flex flex-column flex-md-row justify-content-between gap-2">
                    <div>
                      <div className="text-muted small">Fecha del abono</div>
                      <div className="fw-semibold">{formatDateTime(payment.created_at)}</div>
                    </div>
                    <div className="text-md-end">
                      <div className="text-muted small">Monto</div>
                      <div className="fw-semibold">{currencyFormatter({ currency: "USD", value: payment.amount })}</div>
                    </div>
                  </div>
                  <Row className="mt-3">
                    <Col md="4">
                      <div className="text-muted small">Forma de pago</div>
                      <div>{getPaymentDisplayLabel(payment)}</div>
                    </Col>
                    <Col md="4">
                      <div className="text-muted small">Registro</div>
                      <div>{payment.user_name || payment.user_email || "-"}</div>
                    </Col>
                    <Col md="4">
                      <div className="text-muted small">Dia de caja</div>
                      <div>{payment.cash_operation_day_date || "-"}</div>
                    </Col>
                  </Row>
                  {payment.note && (
                    <div className="mt-3">
                      <div className="text-muted small">Nota</div>
                      <div>{payment.note}</div>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="text-muted">Sin abonos registrados</div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setHistoryModalOpen(false)}>
              Cerrar
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </>
  )
}

PendingPaidItemList.propTypes = {
  item: propTypes.object,
  setTicketPaying: propTypes.func,
  openTicketModal: propTypes.func,
  isCashOperationDayOpen: propTypes.bool,
  showReceivableAmounts: propTypes.bool,
}

export default PendingPaidItemList
